import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { formatCurrency, formatDuration } from '@/lib/i18n';
import { GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import {
  elapsedSecondsAt,
  isRunning,
  loadGuestResourceSessions,
  resourceTypeLabelKey,
  type ResourceSessionsSnapshot,
} from '@/lib/resourceSessions';
import { useResync } from '@/Hooks/useResync';
import { Card, CardContent } from '@/components/ui/card';
import { useAppSelector } from '@/Redux/store';

/**
 * How often the server is asked again for the money.
 *
 * Thirty seconds, not one: the amount is the server's to compute and every one
 * of these is a request from a phone on venue Wi-Fi, several of which are
 * sitting on the same table. The clock in between is advanced locally (see
 * `elapsedSecondsAt`), so the guest sees a moving timer at one request a
 * half-minute rather than a frozen one at sixty.
 *
 * Faster than every other surface's backstop for the one reason that matters
 * here: nothing pushes this. `RealtimeService.onResourceSessionEvent` fans
 * `resource_session.*` out to `branch:` and `resource:`, and a guest token may
 * enter neither, so for this card the poll is not a net under a socket — it is
 * the only way the figure ever moves.
 */
const REFRESH_MS = 30_000;

/** The visible clock's own beat. */
const TICK_MS = 1_000;

/**
 * What is running on this guest's table, and what it has cost so far.
 *
 * A waiter attaches a PlayStation, a billiards table or an hour in the kids'
 * area to the table, and until now the guest saw nothing at all until the
 * charge appeared on the bill — an invisible hour, then a surprise at the till.
 * This is the whole of the fix: name it, count it, and say out loud that it
 * lands on the same check as the food.
 *
 * Deliberately self-contained. It is mounted beside the bill rather than woven
 * into it, so a route the backend has not deployed yet, a 404, or a venue with
 * the transparency setting off costs the guest this block and nothing else —
 * the bill itself is rendered by its own fetch and is never waiting on this one.
 */
export function ResourceSessions() {
  const { t, i18n } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const sessionId = session?.sessionId ?? null;

  const [snapshot, setSnapshot] = useState<ResourceSessionsSnapshot | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(() => {
    if (!sessionId) return;
    loadGuestResourceSessions()
      .then((sessions) => setSnapshot({ sessions, fetchedAt: Date.now() }))
      .catch(() => {
        /*
         * Quietly. This is a read, not a request the guest made, and the app it
         * sits in is built for a basement and a captive portal: a toast for
         * every failed poll would shout at a guest who asked for nothing.
         *
         * The last reading is kept rather than cleared, for the same reason the
         * menu keeps its cache on screen when the network goes: blanking a
         * running meter on one dropped packet tells the guest it has stopped,
         * and it has not. A first attempt that fails has nothing to keep, so
         * `snapshot` stays null and nothing is drawn at all.
         */
      });
  }, [sessionId]);

  useEffect(load, [load]);

  /*
   * The visibility-aware poll this card used to run by hand, now the app's one
   * answer to "how does this screen catch up" — same skip while hidden, same
   * re-read the moment the guest comes back, and the socket reconnect thrown in
   * for free. It was written here first, and a second, differently-shaped copy
   * of it on every other guest surface is how two screens end up disagreeing
   * about how long stale is allowed to last.
   *
   * The clock is wall-clock based (see `elapsedSecondsAt`), so nothing about
   * the timer needs catching up when the phone comes out of a pocket. Only the
   * money does.
   */
  useResync(load, REFRESH_MS);

  const ticking = snapshot?.sessions.some(isRunning) ?? false;

  useEffect(() => {
    // No timer at all when nothing is counting: a paused meter and an empty
    // table both re-render this screen once a second for no reason otherwise.
    if (!ticking) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [ticking]);

  // An empty list is the answer for a table with nothing running AND for a
  // venue that has switched this off, which the server signals the same way.
  // Neither one gets a heading with nothing under it.
  if (!snapshot || snapshot.sessions.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">{t('guest.resources.title')}</h2>

      <div className="space-y-2">
        {snapshot.sessions.map((resourceSession) => {
          const typeKey = resourceTypeLabelKey(resourceSession.resourceType);
          const running = isRunning(resourceSession);
          const elapsed = elapsedSecondsAt(resourceSession, snapshot.fetchedAt, now);
          const currency =
            resourceSession.currency || session?.currency || GUEST_FALLBACK_CURRENCY;

          return (
            <Card key={resourceSession.id}>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <Timer className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {resourceSession.resourceName}
                      </p>
                      {typeKey && (
                        <p className="truncate text-xs text-muted-foreground">{t(typeKey)}</p>
                      )}
                    </div>
                  </div>
                  {/* The server's own figure, formatted by the app's one money
                      formatter and computed by nothing here. `formatCurrency`
                      is handed the decimal string it arrived as, so a venue on
                      KWD gets three places and no amount is re-derived in
                      float on the way to the screen. */}
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatCurrency(resourceSession.accruedAmount, currency, i18n.language)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  {running ? (
                    <span>{t('guest.resources.elapsed')}</span>
                  ) : (
                    <span className="rounded-full border border-line bg-accent-soft px-2.5 py-0.5 font-medium text-accent">
                      {t('guest.resources.paused')}
                    </span>
                  )}
                  {/* `dir="ltr"` isolates the clock: "1:04:22" is a run of
                      digits and colons with no direction of its own, and left
                      to the paragraph's Arabic it reorders into a time the
                      guest never saw on the wall. */}
                  <span dir="ltr" className="shrink-0 tabular-nums">
                    {formatDuration(elapsed)}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* The point of the whole block. A guest who reads the timer but does not
          know where the charge goes is still surprised at the till, which is
          the thing this exists to prevent. */}
      <p className="text-xs text-muted-foreground">{t('guest.resources.sameBill')}</p>
    </section>
  );
}
